// src/features/adminCompanies/adminCompaniesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../utils/constant';

// Async thunk to create a company
export const createCompany = createAsyncThunk(
    'adminCompanies/createCompany',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${API_URL}/companies`, formData); // Replace with your API endpoint
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Async thunk to get companies
export const getCompanies = createAsyncThunk(
    'adminCompanies/getCompanies',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/companies`); // Replace with your API endpoint
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Async thunk to edit a company
export const editCompany = createAsyncThunk(
    'adminCompanies/editCompany',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${API_URL}/companies/${id}`, formData); // Replace with your API endpoint
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Async thunk to delete a company
export const deleteCompany = createAsyncThunk(
    'adminCompanies/deleteCompany',
    async (id, { rejectWithValue }) => {
        try {
            await axios.delete(`${API_URL}/companies/${id}`); // Replace with your API endpoint
            return id; // Return the deleted id for updating state
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

const adminCompaniesSlice = createSlice({
    name: 'adminCompanies',
    initialState: {
        companies: [],
        formData: {
            company_name: '',
            user_name: '',
            email: '',
            mobile_no: '',
            password: '',
            confirm_password: '',
            trade: '',
            company_type: '',
            company_pan_number: '',
            doi: '',
            register_address: '',
            gst_number: '',
            nature_of_business: ''
        },
        loading: false,
        error: null,
        success: false
    },
    reducers: {
        // Action to update formData in the store
        updateFormData: (state, action) => {
            state.formData = {
                ...state.formData,
                ...action.payload
            };
        },
        resetFormData: (state) => {
            state.formData = {
                company_name: '',
                user_name: '',
                email: '',
                mobile_no: '',
                password: '',
                confirm_password: '',
                trade: '',
                company_type: '',
                company_pan_number: '',
                doi: '',
                register_address: '',
                gst_number: '',
                nature_of_business: ''
            };
            state.success = false;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(createCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.error = null;
                state.companies.push(action.payload); // Add the new company to the list
            })
            .addCase(createCompany.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action.payload || 'Failed to create company';
            })
            .addCase(getCompanies.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCompanies.fulfilled, (state, action) => {
                state.loading = false;
                state.companies = action.payload; // Set companies list
            })
            .addCase(getCompanies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch companies';
            })
            .addCase(editCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(editCompany.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.companies.findIndex(company => company.id === action.payload.id);
                if (index !== -1) {
                    state.companies[index] = action.payload; // Update the edited company
                }
            })
            .addCase(editCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to edit company';
            })
            .addCase(deleteCompany.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteCompany.fulfilled, (state, action) => {
                state.loading = false;
                state.companies = state.companies.filter(company => company.id !== action.payload); // Remove deleted company
            })
            .addCase(deleteCompany.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete company';
            });
    }
});

export const { updateFormData, resetFormData } = adminCompaniesSlice.actions;

export default adminCompaniesSlice.reducer;
